import React, { useState } from 'react';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import { aiService } from '../../../../services/aiService';
import { productService } from '../../../../services/productService';

const VariationsTab = ({ productData, onFieldChange }) => {
  const [newVariation, setNewVariation] = useState({
    type: 'cor',
    value: '',
    sku: '',
    stock: '',
    price: '',
    imageUrl: ''
  });
  const [analyzingVariations, setAnalyzingVariations] = useState(false);
  const [variationSuggestions, setVariationSuggestions] = useState([]);
  const [editingVariationId, setEditingVariationId] = useState(null);
  const [editingVariationData, setEditingVariationData] = useState({});

  const variations = productData?.variations || [];

  const typeOptions = [
    { value: 'cor', label: 'Cor' },
    { value: 'tamanho', label: 'Tamanho' },
    { value: 'modelo', label: 'Modelo' },
    { value: 'voltagem', label: 'Voltagem' }
  ];

  const handleAddVariation = () => {
    if (!newVariation?.value || !newVariation?.sku) {
      alert('Preencha pelo menos o valor e SKU da variação');
      return;
    }

    const variation = {
      id: Date.now(),
      ...newVariation,
      stock: parseInt(newVariation?.stock) || 0,
      price: parseFloat(newVariation?.price) || 0
    };

    onFieldChange('variations', [...variations, variation]);
    
    setNewVariation({
      type: 'cor',
      value: '',
      sku: '',
      stock: '',
      price: '',
      imageUrl: ''
    });
  };

  const handleRemoveVariation = (id) => {
    onFieldChange('variations', variations?.filter(v => v?.id !== id));
  };

  const handleStartEditVariation = (variation) => {
    setEditingVariationId(variation?.id);
    setEditingVariationData({ ...variation });
  };

  const handleCancelEditVariation = () => {
    setEditingVariationId(null);
    setEditingVariationData({});
  };

  const handleSaveVariation = () => {
    const updatedVariations = variations?.map(v => 
      v?.id === editingVariationId ? editingVariationData : v
    );
    onFieldChange('variations', updatedVariations);
    setEditingVariationId(null);
    setEditingVariationData({});
  };

  const handleEditFieldChange = (field, value) => {
    setEditingVariationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAnalyzeVariations = async () => {
    try {
      setAnalyzingVariations(true);
      setVariationSuggestions([]);
      
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'info',
          message: 'Analisando produtos similares com IA...',
          progress: 50
        }
      }));
      
      // Get all products from database
      const { products } = await productService?.getAll();
      
      if (!products || products?.length === 0) {
        throw new Error('Nenhum produto encontrado para análise');
      }
      
      // Build prompt for AI analysis
      const currentProduct = {
        name: productData?.name,
        sku: productData?.sku,
        brand: productData?.brand,
        category: productData?.category
      };
      
      const productsForAnalysis = products
        ?.filter(p => p?.id !== productData?.id && p?.productType !== 'kit')
        ?.slice(0, 50) // Limit to 50 products for token efficiency
        ?.map(p => ({
          id: p?.id,
          name: p?.name,
          sku: p?.sku,
          brand: p?.brand,
          category: p?.category
        }));
      
      const prompt = `Você é um especialista em análise de catálogo de produtos. Analise o produto atual e identifique produtos similares que podem ser variações.

=== PRODUTO ATUAL ===
${JSON.stringify(currentProduct, null, 2)}

=== PRODUTOS CADASTRADOS ===
${JSON.stringify(productsForAnalysis, null, 2)}

=== CRITÉRIOS PARA IDENTIFICAR VARIAÇÕES ===
1. Nome base semelhante (ex: "Camiseta Modelo X" e "Camiseta Modelo X Azul")
2. Mesmo SKU base ou padrão similar (ex: "ABC-001" e "ABC-002")
3. Mesma marca E mesma categoria
4. Diferença apenas em:
   - Cor (azul, vermelho, preto, branco, etc.)
   - Tamanho (P, M, G, GG, ou dimensões numéricas)
   - Voltagem (110V, 220V, Bivolt)
   - Modelo/versão (V1, V2, Plus, Pro, etc.)

=== EXEMPLOS DE VARIAÇÕES VÁLIDAS ===
- "Mesa Educativa Azul" e "Mesa Educativa Vermelha" → VARIAÇÃO (mesma mesa, cores diferentes)
- "Cadeira Gamer RGB" e "Cadeira Gamer Preta" → VARIAÇÃO (mesma cadeira, cores diferentes)
- "Ventilador 110V" e "Ventilador 220V" → VARIAÇÃO (mesmo produto, voltagens diferentes)

=== EXEMPLOS DE NÃO-VARIAÇÕES ===
- "Mesa Educativa" e "Cadeira Educativa" → NÃO (produtos diferentes)
- "Brinquedo Marca A" e "Brinquedo Marca B" → NÃO (marcas diferentes)
- "Carrinho de Controle" e "Boneca de Pano" → NÃO (categorias diferentes)

=== FORMATO DE RESPOSTA ===
Retorne um JSON com produtos que são variações:
{
  "suggestions": [
    {
      "productId": "id do produto",
      "productName": "nome do produto",
      "reason": "motivo específico para agrupar (ex: mesmo modelo, apenas cor diferente)",
      "confidence": "high" | "medium" | "low",
      "variationType": "cor" | "tamanho" | "voltagem" | "modelo"
    }
  ]
}

Se não encontrar variações válidas, retorne: { "suggestions": [] }

APENAS sugira produtos que sejam REALMENTE variações do produto atual.`;
      
      const response = await aiService?.generateContent(prompt, { 
        maxTokens: 1000,
        responseFormat: 'json'
      });
      
      if (response?.success) {
        let result;
        try {
          result = JSON.parse(response?.content);
        } catch {
          result = { suggestions: [] };
        }
        
        if (result?.suggestions && result?.suggestions?.length > 0) {
          setVariationSuggestions(result?.suggestions);
          window.dispatchEvent(new CustomEvent('statusUpdate', {
            detail: {
              type: 'success',
              message: `${result?.suggestions?.length} variação(ões) encontrada(s) pela IA`,
              progress: 100
            }
          }));
        } else {
          window.dispatchEvent(new CustomEvent('statusUpdate', {
            detail: {
              type: 'info',
              message: 'Nenhuma variação similar encontrada',
              progress: 100
            }
          }));
        }
      } else {
        throw new Error(response?.error || 'IA não disponível');
      }
    } catch (error) {
      console.error('Error analyzing variations:', error);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: error?.message || 'Erro ao analisar variações',
          progress: 0
        }
      }));
    } finally {
      setAnalyzingVariations(false);
    }
  };

  const getConfidenceBadgeColor = (confidence) => {
    switch (confidence) {
      case 'high':
        return 'bg-green-500/20 text-green-700 border-green-500/30';
      case 'medium':
        return 'bg-yellow-500/20 text-yellow-700 border-yellow-500/30';
      case 'low':
        return 'bg-gray-500/20 text-gray-700 border-gray-500/30';
      default:
        return 'bg-gray-500/20 text-gray-700 border-gray-500/30';
    }
  };

  const getConfidenceLabel = (confidence) => {
    switch (confidence) {
      case 'high':
        return 'Alta';
      case 'medium':
        return 'Média';
      case 'low':
        return 'Baixa';
      default:
        return 'Desconhecida';
    }
  };

  return (
    <div className="space-y-6">
      {/* AI Variation Analysis - UPDATED WITH BLUE-PURPLE GRADIENT */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon name="Sparkles" size={18} className="text-blue-600" />
            <span className="text-sm font-semibold text-gray-900">Analisar Variações com IA</span>
          </div>
          <Button
            onClick={handleAnalyzeVariations}
            disabled={analyzingVariations}
            variant="primary"
            size="sm"
          >
            {analyzingVariations ? (
              <>
                <Icon name="Loader" size={14} className="mr-1 animate-spin" />
                Analisando...
              </>
            ) : (
              <>
                <Icon name="Search" size={14} className="mr-1" />
                Analisar
              </>
            )}
          </Button>
        </div>
        <p className="text-xs text-gray-600">
          A IA detectará automaticamente produtos similares que diferem apenas por cor, tamanho, voltagem ou modelo
        </p>
        
        {variationSuggestions?.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-semibold text-gray-900">Sugestões de Agrupamento:</p>
            {variationSuggestions?.map((suggestion, index) => (
              <div key={index} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{suggestion?.productName}</p>
                    <p className="text-xs text-gray-600 mt-1">{suggestion?.reason}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs border ${getConfidenceBadgeColor(suggestion?.confidence)}`}>
                        Confiança: {getConfidenceLabel(suggestion?.confidence)}
                      </span>
                      {suggestion?.variationType && (
                        <span className="inline-block px-2 py-0.5 rounded text-xs bg-blue-500/10 text-blue-700 border border-blue-500/20">
                          Tipo: {suggestion?.variationType}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('statusUpdate', {
                        detail: {
                          type: 'info',
                          message: 'Funcionalidade de agrupamento automático em desenvolvimento',
                          progress: 100
                        }
                      }));
                    }}
                  >
                    Agrupar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Add New Variation */}
      <div className="bg-muted/50 border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Plus" size={18} />
          Adicionar Nova Variação
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Select
            label="Tipo de Variação"
            value={newVariation?.type}
            onChange={(e) => setNewVariation({ ...newVariation, type: e?.target?.value })}
            options={typeOptions}
          />
          
          <Input
            label="Valor"
            value={newVariation?.value}
            onChange={(e) => setNewVariation({ ...newVariation, value: e?.target?.value })}
            placeholder="Ex: Azul, M, 110V"
          />
          
          <Input
            label="SKU"
            value={newVariation?.sku}
            onChange={(e) => setNewVariation({ ...newVariation, sku: e?.target?.value })}
            placeholder="SKU-001"
          />
          
          <Input
            label="Estoque"
            type="number"
            value={newVariation?.stock}
            onChange={(e) => setNewVariation({ ...newVariation, stock: e?.target?.value })}
            placeholder="0"
          />
          
          <Input
            label="Preço"
            type="number"
            step="0.01"
            value={newVariation?.price}
            onChange={(e) => setNewVariation({ ...newVariation, price: e?.target?.value })}
            placeholder="0.00"
          />
          
          <Input
            label="URL da Imagem"
            value={newVariation?.imageUrl}
            onChange={(e) => setNewVariation({ ...newVariation, imageUrl: e?.target?.value })}
            placeholder="https://..."
          />
        </div>
        
        <Button
          onClick={handleAddVariation}
          iconName="Plus"
        >
          Adicionar Variação
        </Button>
      </div>
      {/* Existing Variations */}
      {variations?.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-foreground">Variações Cadastradas</h3>
          {variations?.map((variation) => (
            <div key={variation?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
              {editingVariationId === variation?.id ? (
                // EDIT MODE - Inline editor for individual variation
                (<div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Tipo</label>
                      <Select
                        value={editingVariationData?.type}
                        onChange={(e) => handleEditFieldChange('type', e?.target?.value)}
                        options={typeOptions}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Valor</label>
                      <Input
                        value={editingVariationData?.value}
                        onChange={(e) => handleEditFieldChange('value', e?.target?.value)}
                        placeholder="Ex: Azul, P, 110V"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">SKU</label>
                      <Input
                        value={editingVariationData?.sku}
                        onChange={(e) => handleEditFieldChange('sku', e?.target?.value)}
                        placeholder="SKU único"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Estoque</label>
                      <Input
                        type="number"
                        value={editingVariationData?.stock}
                        onChange={(e) => handleEditFieldChange('stock', e?.target?.value)}
                        placeholder="Quantidade"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Preço</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingVariationData?.price}
                        onChange={(e) => handleEditFieldChange('price', e?.target?.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">URL da Imagem</label>
                      <Input
                        value={editingVariationData?.imageUrl}
                        onChange={(e) => handleEditFieldChange('imageUrl', e?.target?.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      onClick={handleCancelEditVariation}
                      variant="outline"
                      size="sm"
                    >
                      <Icon name="X" size={14} className="mr-1" />
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSaveVariation}
                      variant="primary"
                      size="sm"
                    >
                      <Icon name="Check" size={14} className="mr-1" />
                      Salvar
                    </Button>
                  </div>
                </div>)
              ) : (
                // VIEW MODE - Display variation with edit button
                (<div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                        {typeOptions?.find(opt => opt?.value === variation?.type)?.label || variation?.type}
                      </span>
                      <span className="font-semibold text-foreground">{variation?.value}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground text-xs">SKU:</span>
                        <p className="text-foreground font-medium">{variation?.sku}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-xs">Estoque:</span>
                        <p className="text-foreground font-medium">{variation?.stock || 0}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-xs">Preço:</span>
                        <p className="text-foreground font-medium">R$ {parseFloat(variation?.price || 0)?.toFixed(2)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-xs">Imagem:</span>
                        <p className="text-foreground font-medium truncate">{variation?.imageUrl ? '✓' : '-'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleStartEditVariation(variation)}
                      variant="outline"
                      size="sm"
                    >
                      <Icon name="Edit" size={14} className="mr-1" />
                      Editar
                    </Button>
                    <Button
                      onClick={() => handleRemoveVariation(variation?.id)}
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <Icon name="Trash2" size={14} />
                    </Button>
                  </div>
                </div>)
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VariationsTab;